import { z } from 'zod/v4-mini';

import { FileToUpload } from '@/models/FileModel';
import { Profession, professionSchema } from '@/models/ProfessionModel.ts';
import { Schema, cz } from '@/utils/zodUtils.ts';

export type ChatVisibilityType = 'all' | 'practitioners';

export type ReadReceipt = {
  userId: string;
  userType: string;
  firstname: string;
  lastname: string;
  qualification?: Profession;
  messageId: string;
  read: boolean;
  readAt?: string;
};

export type Message = {
  id: string;

  userId: string;
  userType: string;
  firstname: string;
  lastname: string;
  qualification?: Profession;
  subjectId: string;

  visibility: ChatVisibilityType;
  text?: string;
  deleted: boolean;
  deletedAt?: string;
  readReceipts: ReadReceipt[];
  attachments: { url: string; name: string }[];

  createdAt: string;
};

export type UnreadMessageCount = {
  total: number;
  practitioners: number;
  patients: number;
  gestationalNoInsulinPatients: number;
  practitionerToPractitioners: number;
};

const userSchema = cz.transform(
  z.discriminatedUnion('user_type', [
    z.object({
      user_type: z.literal('patient'),
      patient: z.object({
        id: z.string(),
        firstname: z.string(),
        lastname: z.string(),
      }),
    }),
    z.object({
      user_type: z.literal('practitioner'),
      practitioner: z.object({
        id: z.string(),
        firstname: z.string(),
        lastname: z.string(),
        qualification: professionSchema,
      }),
    }),
  ]),
  data =>
    data.user_type === 'patient'
      ? {
          userType: 'patient',
          userId: data.patient.id,
          firstname: data.patient.firstname,
          lastname: data.patient.lastname,
          qualification: undefined,
        }
      : {
          userType: 'practitioner',
          userId: data.practitioner.id,
          firstname: data.practitioner.firstname,
          lastname: data.practitioner.lastname,
          qualification: data.practitioner.qualification,
        },
);

const readReceiptSchema: Schema<ReadReceipt> = z.intersection(
  userSchema,
  cz.transform(
    z.object({
      message_id: z.string(),
      read: z.boolean(),
      read_date: cz.optional(cz.datetime()),
    }),
    data => ({
      messageId: data.message_id,
      read: data.read,
      readAt: data.read_date,
    }),
  ),
);

export const messageSchema: Schema<Message> = z.intersection(
  userSchema,
  cz.transform(
    z.object({
      id: z.string(),
      subject_id: z.string(),

      visibility: z.literal(['all', 'practitioners']),
      content_text: cz.optional(z.string()),
      deleted: z.boolean(),
      deleted_at: cz.optional(cz.datetime()),
      read_receipts: cz.forcedArray(readReceiptSchema),
      attachments: cz.forcedArray(
        z.object({
          url: z.string(),
          name: z.string(),
        }),
      ),
      created_at: cz.datetime(),
    }),
    data => ({
      id: data.id,
      subjectId: data.subject_id,

      visibility: data.visibility,
      text: data.content_text,
      deleted: data.deleted,
      deletedAt: data.deleted_at,
      readReceipts: data.read_receipts,
      attachments: data.attachments,

      createdAt: data.created_at,
    }),
  ),
);

export const unreadMessageCountSchema: Schema<UnreadMessageCount> =
  cz.transform(
    z.object({
      total_messages: z.number(),
      patient_messages: z.number(),
      practitioner_messages: z.number(),
      gestational_no_insulin_patient_messages: z.number(),
      practitioner_to_practitioner_messages: z.number(),
    }),
    data => ({
      total: data.total_messages,
      practitioners: data.practitioner_messages,
      patients: data.patient_messages,
      practitionerToPractitioners: data.practitioner_to_practitioner_messages,
      gestationalNoInsulinPatients:
        data.gestational_no_insulin_patient_messages,
    }),
  );

export type CreateMessage = {
  visibility: ChatVisibilityType;
  content_text?: string;
  attachments?: FileToUpload[];
};

export const isMessageRead = (message: Message, practitionerId: string) => {
  if (message.deleted || isMessageSender(message, practitionerId)) {
    return true;
  }
  const receipt = message.readReceipts.find(r => r.userId === practitionerId);
  if (!receipt) {
    return true;
  }
  return receipt.read;
};

export const isMessageSender = (message: Message, practitionerId: string) =>
  message.userId === practitionerId;
